/**
 * OverallScoreBadge Component
 *
 * Shared badge styling for displaying overall response scores with optional trophy icon.
 * Used by Response Detail and Scoring views to keep winner/non-winner visuals aligned.
 */

import React from 'react';

export type OverallScoreBadgeSize = 'desktop' | 'mobile';

export interface OverallScoreBadgeProps {
  score: number;
  showTrophy?: boolean;
  size?: OverallScoreBadgeSize;
  className?: string;
}

export const OverallScoreBadge: React.FC<OverallScoreBadgeProps> = ({
  score,
  showTrophy = false,
  size = 'desktop',
  className = ''
}) => {
  const formattedScore = Number.isFinite(score) ? score.toFixed(2) : '—';
  const baseClasses = 'inline-flex items-center rounded text-[11px] font-semibold whitespace-nowrap flex-shrink-0 border border-amber-400 text-amber-100 bg-transparent';
  const sizeClasses = size === 'mobile'
    ? 'px-1 py-0.5 text-[10px] gap-0.5'
    : 'px-1.5 py-0.5 gap-1';

  const ariaLabel = `Overall score ${formattedScore}`;

  return (
    <span
      className={`${baseClasses} ${sizeClasses} ${className}`}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {showTrophy && (
        <span aria-hidden="true" className="text-sm leading-none">
          🏆
        </span>
      )}
      <span className="text-amber-200">Overall</span>
      <span>{formattedScore}</span>
    </span>
  );
};
